import { FieldTypes } from '../../../constants/FieldTypes'
import { DropdownField, DropdownOptionItem, FieldMap, InfoStage } from '../../../types/forms/fields'
import { InsuranceInfo, InsuranceInfoOption } from '../../../types/users/user'
import { makeAllFieldsAdmin } from '../../../utils/fields'
import {
  getPregnancyPrimaryCoverageField,
  getPregnancySecondaryCoverageField,
} from '../../assessment/coverage'
import { coverageWillChangeFields } from './coverageWillChange'
import { multipleCoverageOptionsFields } from './multipleCoverageOptions'
import { noCoverageFields } from './noCoverage'
import { notPregnantFields } from './notPregnant'
import { selectCoverageOption } from './utils'

export const insuranceInfoOptions: DropdownOptionItem<InsuranceInfoOption>[] = [
  {
    text: 'I am covered by one insurance plan (I am not covered by Medicaid)',
    id: 'one-plan',
  },
  {
    text: 'I am covered by two insurance plans (can include Medicaid)',
    id: 'two-plans',
  },
  {
    text: 'I am covered by Medicaid or a Medicaid Managed plan only (example: Health First Medicaid, UHC Community Plan etc.)',
    id: 'medicaid',
  },
  {
    text: "I don't have insurance coverage",
    id: 'no-coverage',
  },
  {
    text: 'I have insurance but it will change/end before I deliver',
    id: 'coverage-will-change',
  },
  {
    text: 'I have multiple options for coverage',
    id: 'multiple-options',
    redFlagReason: 'Multiple coverage options',
  },
  {
    text: 'I am not pregnant but would like information on coverage',
    id: 'not-pregnant',
  },
]

const twoPlansNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v => selectCoverageOption('two-plans')(v) && v?.onePlanIsMedicaid === false,
  data: [
    {
      type: 'span',
      children: [
        {
          text: `Please note your assessment will include information for your primary insurance only. 

Secondary coverage may or may not cover the deductible or coinsurance from your primary insurer.  

If you'd like more information on your secondary insurance, please schedule a call with us at the end of the form.`,
        },
      ],
    },
  ],
}

export const getHasNonMedicaidCoverage = (
  option: string | undefined,
  coverageWillChangeDescription: string | undefined,
  multipleOptionsDescription: string | undefined,
  confirmPartnerPlan: boolean | undefined,
) => {
  if (option === 'one-plan') return true
  if (option === 'two-plans') return true
  if (
    coverageWillChangeDescription &&
    ['employment-coverage-will-change', 'added-to-another-plan'].includes(
      coverageWillChangeDescription,
    )
  )
    return true
  if (
    multipleOptionsDescription &&
    ['partner-individual'].includes(multipleOptionsDescription) &&
    confirmPartnerPlan
  )
    return true
  if (multipleOptionsDescription && multipleOptionsDescription === 'partner-employer') return true
  return false
}

const primaryCoverageNote: InfoStage = {
  info: true,
  name: 'Notice',
  condition: v => v?.option === 'two-plans',
  data: [
    {
      type: 'span',
      children: [
        {
          text: `If you are covered by more than one insurance plan, your primary coverage is likely the plan you've been on the longest. 
Please do additional research to confirm your primary insurer.`,
        },
      ],
    },
  ],
}

const primaryCoverageNote2: InfoStage = {
  info: true,
  name: 'Notice',
  condition: selectCoverageOption('two-plans'),
  data: [
    {
      type: 'span',
      children: [
        {
          text: 'If one of your plans is Medicaid, enter your other plan as your primary coverage.',
        },
      ],
    },
  ],
}

export const insuranceInfoOptionField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  options: insuranceInfoOptions,
  labels: [{ text: 'Which of these describes your coverage?' }],
  placeholder: 'Coverage Description',
}

// TODO: rework insurance plans fields to work with primary/secondary coverage setup
export const insuranceInfoStage: FieldMap = {
  name: 'Insurance Info',
  initExpanded: true,
  calculate: [
    {
      field: 'confirmPartnerPlan',
      updates: {
        confirmPartnerPlan: (confirmValue: any) => {
          if (!confirmValue) return undefined
          return true
        },
        multipleOptionsDescription: (confirmValue: any, allValues: any) => {
          if (confirmValue === false) return undefined
          return allValues.multipleOptionsDescription
        },
      },
    },
  ],
  children: {
    option: insuranceInfoOptionField,
    twoPlansNote,
    ...noCoverageFields,
    ...coverageWillChangeFields,
    ...multipleCoverageOptionsFields,
    ...notPregnantFields,
    primaryCoverageNote,
    primaryCoverageNote2,
  },
}

export const getInsuranceInfoStage = (
  insuranceInfo: Partial<InsuranceInfo> | undefined,
  withCallIn: boolean,
  admin: boolean,
  forGynForm?: boolean,
): FieldMap => {
  const children = {
    ...insuranceInfoStage.children,
  }

  const primaryCoverageField = getPregnancyPrimaryCoverageField(insuranceInfo, admin, withCallIn)
  if (primaryCoverageField) {
    children.primaryCoverage = primaryCoverageField
  }

  if (!forGynForm) {
    const secondaryCoverageField = getPregnancySecondaryCoverageField(
      insuranceInfo,
      admin,
      withCallIn,
    )
    if (secondaryCoverageField) {
      children.secondaryCoverage = secondaryCoverageField
    }
  }
  const stage = {
    ...insuranceInfoStage,
    children,
  }
  return admin ? makeAllFieldsAdmin(stage) : stage
}
